<template>
    <grid
        ref="grid"
        :column-defs="columnDefs"
        :fetch-callback="fetchCallback"
    >
        <date-range-picker
            :range-options="predefinedRanges"
            :value="dateRange"
            :date-format="userUnicodeDateFormat"
            @input="handleDaterangeChange"
        ></date-range-picker>
        <responsive-dropdown
            id="registration-list-filters"
            :icon="['fas', 'filter']"
            :items="dropdownFilterOptions"
            variant="primary"
        >
            {{ filter.label }}
        </responsive-dropdown>        

        <b-button-group aria-label="Export table data">
            <responsive-button
                :icon="['fas', 'file-excel']"
                :href="exportExcelHref"
                @click="generatingExcelReportSuccessToast"
                variant="primary"
            >
            </responsive-button>
        </b-button-group>
        <b-button-group>
            <more-filters-dropdown :icon="['fas', 'circle-info']">
                <responsive-button
                    :clickable="false"
                    :responsiveness="false"
                    variant="secondary"
                >
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="text-left">Total Signups</div>
                        <div><b-badge variant="light">{{ totalUsers }}</b-badge></div>
                    </div>
                </responsive-button>
                <responsive-button
                    :clickable="false"
                    :responsiveness="false"
                    variant="secondary"
                >
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="text-left">Active Signups</div>
                        <div><b-badge variant="light">{{ activeUsers }}</b-badge></div>
                    </div>
                </responsive-button>
                <responsive-button
                    :clickable="false"
                    :responsiveness="false"
                    variant="secondary"
                >
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="text-left">Active Signups<br/>with No Subscription</div>
                        <div><b-badge variant="light">{{ activeWithNoSubscription }}</b-badge></div>
                    </div>
                </responsive-button>
                <responsive-button
                    :clickable="false"
                    :responsiveness="false"
                    variant="secondary"
                >
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="text-left">Active Signups<br/>with Active Subscription</div>
                        <div><b-badge variant="light">{{ activeWithActiveSubscription }}</b-badge></div>
                    </div>
                </responsive-button>
                <responsive-button
                    :clickable="false"
                    :responsiveness="false"
                    variant="secondary"
                >
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="pr-2 text-left">Active Signups<br/>with Cancelled Subscription</div>
                        <div><b-badge variant="light">{{ activeWithCancelledSubscription }}</b-badge></div>
                    </div>
                </responsive-button>
            </more-filters-dropdown>
        </b-button-group>
        <responsive-button
            :icon="['fas', 'arrows-rotate']"
            variant="primary"
            @click="refresh"
            class="ml-1"
        >
            Refresh
        </responsive-button>
    </grid>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../../../api';
import DateRangePicker, {
    ALL_DATES,
    LAST_MONTH,
    LAST_YEAR,
    nameToRange,
    THIS_MONTH,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
} from '../../../generic/date-range-picker/date-range-picker.vue';
import Grid from '../../../generic/grid/grid.vue';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../../../generic/responsive-dropdown/responsive-dropdown.vue';
import MoreFiltersDropdown from '../../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';

import {
    SUBSCRIBER_REGISTRATIONS_GRID_ENDPOINT,
    SUBSCRIBER_REGISTRATIONS_EXCEL_ENDPOINT,
    SUBSCRIBER_REGISTRATIONS_STATISTICS_ENDPOINT,
} from '../../../../endpoints';

const activeFilter = { value: 'ACTIVE_REGISTRATIONS', label: 'View Active' };
const allFilter = { value: 'ALL_REGISTRATIONS', label: 'View All' };


export default {
    name: 'SubscriberRegistrationList',
    components: {
        DateRangePicker,
        Grid,
        ResponsiveButton,
        ResponsiveDropdown,
        MoreFiltersDropdown,
        actionsCellRenderer: ActionsCellRenderer,
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(
                    SUBSCRIBER_REGISTRATIONS_GRID_ENDPOINT(),
                    {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            from_date: this.fromDateRequest,
                            to_date: this.toDateRequest,
                            only_active: this.onlyActive,
                        },
                    }
                );
            }
        },
        dropdownFilterOptions() {
            return [
                {
                    icon: ['fas', 'filter'],
                    label: activeFilter.label,
                    active: this.filter === activeFilter.value,
                    click: () => {
                        this.filter = activeFilter;
                        this.refresh();
                    },
                },
                {
                    icon: ['fas', 'filter'],
                    label: allFilter.label,
                    active: this.filter === allFilter,
                    click: () => {
                        this.filter = allFilter;
                        this.refresh();
                    },
                },
            ];
        },        
        fromDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        toDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        exportExcelHref() {
            return (
                `${SUBSCRIBER_REGISTRATIONS_EXCEL_ENDPOINT()}` + 
                `?offset=${this.offset}` +
                `&limit=${this.limit}` +
                `&sort=${this.sort}` +
                `&desc=${this.desc}` +
                `&search=${this.search}` +
                `&from_date=${this.fromDateRequest}` +
                `&to_date=${this.toDateRequest}` +
                `&only_active=${this.onlyActive}`
            );
        },
        onlyActive() {
            return this.filter.value === activeFilter.value;
        }
    },
    data() {
        return {
            offset: 0,
            limit: 20,
            sort: 'id',
            desc: false,
            search: '',
            totalUsers: null,
            activeUsers: null,
            activeWithActiveSubscription: null,
            activeWithCancelledSubscription: null,
            activeWithNoSubscription: null,
            columnDefs: [
                {
                    field: 'id',
                    headerName: 'ID',
                    minWidth: 100,
                    maxWidth: 100,
                    sort: 'desc',
                },
                {
                    field: 'date_joined',
                    headerName: 'Date Joined',
                    type: 'dateColumn',
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    field: 'first_name',
                    headerName: 'First Name',
                },
                {
                    field: 'last_name',
                    headerName: 'Last Name',
                },
                {
                    field: 'email',
                    headerName: 'Email',
                    minWidth: 400,
                },
                {
                    field: 'is_active',
                    headerName: 'Active',
                    type: 'yesNoColumn',
                    minWidth: 100,
                    maxWidth: 100,
                },
                {
                    field: 'is_validated',
                    headerName: 'Validated',
                    type: 'yesNoColumn',
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    field: 'active_subscription',
                    headerName: 'Subscription',
                    type: 'yesNoColumn',
                    sortable: false,
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    headerName: '',
                    maxWidth: 170,
                    minWidth: 170,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        refresh: this.refresh,
                    },
                },
            ],
            filter: activeFilter,
            dateRange: {
                from: nameToRange(LAST_MONTH).from,
                to: nameToRange(THIS_MONTH).to,
            },
            dateRequestFormat: 'yyyy-MM-dd',
            predefinedRanges: [
                TODAY,
                YESTERDAY,
                THIS_MONTH,
                LAST_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                ALL_DATES,
            ],
        }
    },
    mounted() {
        (async () => {
            this.$parent.$emit('busy', true);
            try {
                await this.fetchStatistics();
            } finally {
                this.$parent.$emit('busy', false);
            }
        })();
    },
    methods: {
        async fetchStatistics() {
            try {
                const response = await API.get(SUBSCRIBER_REGISTRATIONS_STATISTICS_ENDPOINT());
                this.totalUsers = response.data.total_users;
                this.activeUsers = response.data.active_users;
                this.activeWithActiveSubscription = response.data.active_with_active_subscription;
                this.activeWithCancelledSubscription = response.data.active_with_cancelled_subscription;
                this.activeWithNoSubscription = response.data.active_with_no_subscription;
            } catch {
                this.errorToast({ text: 'Could not fetch statistics.' });
            }
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.refresh();
        },
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
    },
}
</script>
